import {
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Typography,
  Progress,
  Spin,
  Alert,
} from 'antd';
import {
  WarningOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useSlaSummary } from '@hooks/useService';
import type { EngineerStats } from '@/types/service';

export default function SlaDashboardPage() {
  const { data: mdSummary, isLoading: mdIsLoading } = useSlaSummary();

  if (mdIsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!mdSummary) {
    return <Alert type="warning" message="Дані SLA недоступні" showIcon />;
  }

  const mdEngineerColumns: ColumnsType<EngineerStats> = [
    {
      title: 'Інженер',
      dataIndex: 'engineerName',
      key: 'engineerName',
      // Облікового запису з таким id вже немає — показуємо ідентифікатор
      render: (mdValue: string | null, mdRecord: EngineerStats) =>
        mdValue ?? <Typography.Text type="secondary">{mdRecord.engineerId}</Typography.Text>,
    },
    {
      title: 'Відкритих',
      dataIndex: 'openRequests',
      key: 'openRequests',
      width: 100,
      align: 'right',
    },
    {
      title: 'Завершених',
      dataIndex: 'completedRequests',
      key: 'completedRequests',
      width: 110,
      align: 'right',
    },
    {
      title: 'Порушень SLA',
      dataIndex: 'slaBreaches',
      key: 'slaBreaches',
      width: 130,
      align: 'right',
    },
    {
      title: 'SLA %',
      dataIndex: 'slaCompliancePercent',
      key: 'slaCompliancePercent',
      width: 180,
      // Рахує сервер: завершені до SLA-дедлайну ÷ завершені; null — завершених заявок немає
      render: (mdValue: number | null) => {
        if (mdValue === null || mdValue === undefined) return '—';
        const mdColor = mdValue >= 90 ? '#52c41a' : mdValue >= 70 ? '#faad14' : '#ff4d4f';
        return (
          <Progress
            percent={mdValue}
            size="small"
            strokeColor={mdColor}
            format={(mdPct) => `${mdPct?.toFixed(0)}%`}
          />
        );
      },
    },
    {
      title: 'Середній час (год)',
      dataIndex: 'averageCloseTimeHours',
      key: 'averageCloseTimeHours',
      width: 160,
      align: 'right',
      render: (mdValue: number | null | undefined) =>
        typeof mdValue === 'number' ? mdValue.toFixed(1) : '—',
    },
  ];

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 24 }}>
        SLA Dashboard
      </Typography.Title>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Відкриті заявки"
              value={mdSummary.totalOpenRequests}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="SLA порушено"
              value={mdSummary.totalSlaBreaches}
              prefix={<WarningOutlined />}
              valueStyle={{ color: mdSummary.totalSlaBreaches > 0 ? '#ff4d4f' : undefined }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Середній час закриття (год)"
              value={mdSummary.averageCloseTimeHours}
              precision={1}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Статистика по інженерах">
        <Table<EngineerStats>
          columns={mdEngineerColumns}
          dataSource={mdSummary.engineerStats}
          rowKey="engineerId"
          pagination={false}
          size="middle"
        />
      </Card>
    </>
  );
}
